import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { transactionService } from 'components/api/transactions';
import { Transaction, TransactionParams, TransactionsResponse } from 'types/transaction';
import dayjs from 'dayjs';

interface TransactionsState {
  deposit: {
    data: Transaction[];
    total: number;
    isLoading: boolean;
    error: string | null;
  };
  withdraw: {
    data: Transaction[];
    total: number;
    isLoading: boolean;
    error: string | null;
  };
  pendingWithdraw: {
    data: Transaction[];
    total: number;
    isLoading: boolean;
    error: string | null;
  };
}

export const initialParams: TransactionParams = {
  page: 1,
  limit: 10,
  siteId: 'all',
  status: 'all',
  providerId: 'all',
  dateFrom: dayjs().startOf('month').toISOString(),
  dateTo: dayjs().endOf('month').toISOString(),
};

const initialState: TransactionsState = {
  deposit: {
    data: [],
    total: 0,
    isLoading: false,
    error: null,
  },
  withdraw: {
    data: [],
    total: 0,
    isLoading: false,
    error: null,
  },
  pendingWithdraw: {
    data: [],
    total: 0,
    isLoading: false,
    error: null,
  },
};

export const fetchDepositTransactions = createAsyncThunk(
  'transactions/fetchDepositTransactions',
  async (params: TransactionParams, { rejectWithValue }) => {
    try {
      const response = await transactionService.getDepositTransactions(params);
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || 'Failed to fetch deposit transactions',
      );
    }
  },
);

export const fetchWithdrawTransactions = createAsyncThunk(
  'transactions/fetchWithdrawTransactions',
  async (params: TransactionParams, { rejectWithValue }) => {
    try {
      const response = await transactionService.getWithdrawTransactions(params);
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || 'Failed to fetch withdraw transactions',
      );
    }
  },
);

export const fetchPendingWithdrawals = createAsyncThunk(
  'transactions/fetchPendingWithdrawals',
  async (params: TransactionParams, { rejectWithValue }) => {
    try {
      const response = await transactionService.getPendingWithdrawals(params);
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || 'Failed to fetch pending withdrawals',
      );
    }
  },
);

const transactionsSlice = createSlice({
  name: 'transactions',
  initialState,
  reducers: {
    addPendingWithdrawal: (state, action: PayloadAction<Transaction>) => {
      state.pendingWithdraw.data = [action.payload, ...state.pendingWithdraw.data];
      state.pendingWithdraw.total += 1;
    },
    removePendingWithdrawal: (state, action: PayloadAction<string | number>) => {
      state.pendingWithdraw.data = state.pendingWithdraw.data.filter(
        (t) => t.id !== action.payload,
      );
      state.pendingWithdraw.total = Math.max(0, state.pendingWithdraw.total - 1);
    },
  },
  extraReducers: (builder) => {
    builder
      // Deposit
      .addCase(fetchDepositTransactions.pending, (state, action) => {
        if (!action.meta.arg.isBackground) {
          state.deposit.isLoading = true;
        }
        state.deposit.error = null;
      })
      .addCase(fetchDepositTransactions.fulfilled, (state, action) => {
        state.deposit.isLoading = false;
        state.deposit.data = action.payload.data?.docs || [];
        state.deposit.total = action.payload.data?.totalDocs || 0;
      })
      .addCase(fetchDepositTransactions.rejected, (state, action) => {
        state.deposit.isLoading = false;
        state.deposit.error = action.payload as string;
      })
      // Withdraw
      .addCase(fetchWithdrawTransactions.pending, (state, action) => {
        if (!action.meta.arg.isBackground) {
          state.withdraw.isLoading = true;
        }
        state.withdraw.error = null;
      })
      .addCase(fetchWithdrawTransactions.fulfilled, (state, action) => {
        state.withdraw.isLoading = false;
        state.withdraw.data = action.payload.data?.docs || [];
        state.withdraw.total = action.payload.data?.totalDocs || 0;
      })
      .addCase(fetchWithdrawTransactions.rejected, (state, action) => {
        state.withdraw.isLoading = false;
        state.withdraw.error = action.payload as string;
      })
      // Pending Withdraw
      .addCase(fetchPendingWithdrawals.pending, (state) => {
        state.pendingWithdraw.isLoading = true;
        state.pendingWithdraw.error = null;
      })
      .addCase(
        fetchPendingWithdrawals.fulfilled,
        (state, action: PayloadAction<TransactionsResponse>) => {
          state.pendingWithdraw.isLoading = false;
          state.pendingWithdraw.data = action.payload.data?.docs || [];
          state.pendingWithdraw.total = action.payload.data?.totalDocs || 0;
        },
      )
      .addCase(fetchPendingWithdrawals.rejected, (state, action) => {
        state.pendingWithdraw.isLoading = false;
        state.pendingWithdraw.error = action.payload as string;
      });
  },
});

export const { addPendingWithdrawal, removePendingWithdrawal } = transactionsSlice.actions;
export default transactionsSlice.reducer;
